import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useEvent, usePinchZoom } from 'react-use';

import { GEO_MAP_MAX_SCALE, GEO_MAP_MIN_SCALE } from '#shared';

import { TransitionEventEmitter } from '../transitionEventEmitter';

import type * as d3 from 'd3';

type Opts = {
  targetRef: RefObject<Element>; // usually interactionLayerRef
  fallbackRef: RefObject<HTMLElement>; // e.g. mapRef (canvas) if targetRef is null
  projectionRef: RefObject<d3.GeoProjection>;
  emitter: TransitionEventEmitter;
  sensitivity?: number; // default 1.1
  perFrameClamp?: [number, number]; // default [0.9, 1.1]
  minScale?: number; // default GEO_MAP_MIN_SCALE
  maxScale?: number; // default GEO_MAP_MAX_SCALE
};

export const useProjectionPinchZoom = ({
  targetRef,
  fallbackRef,
  projectionRef,
  emitter,
  sensitivity = 1.1,
  perFrameClamp = [0.9, 1.1],
  minScale = GEO_MAP_MIN_SCALE,
  maxScale = GEO_MAP_MAX_SCALE,
}: Opts) => {
  // outward flags/controls
  const dragLockedRef = useRef(false); // lock drags while multi-touch

  // internals
  const activeTouchesRef = useRef<Set<number>>(new Set());
  const lastPinchDistRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const wasPinchingRef = useRef(false);

  const beginPinch = () => {
    lastPinchDistRef.current = null; // fresh baseline
    dragLockedRef.current = true; // block drags
    emitter.emit({
      type: 'change-start',
      timestamp: Date.now(),
      rotation: projectionRef.current.rotate(),
      scale: projectionRef.current.scale(),
    });
  };
  const endPinch = () => {
    dragLockedRef.current = false;
    lastPinchDistRef.current = null;
    emitter.emit({
      type: 'change-end',
      timestamp: Date.now(),
      rotation: projectionRef.current.rotate(),
      scale: projectionRef.current.scale(),
    });
  };

  // resolve target element once per render (react-use tolerates undefined)

  const target = (targetRef.current || fallbackRef?.current) ?? undefined;

  // narrow handler to PointerEvent; keep type as Event for useEvent compatibility
  const onDown = useCallback((ev: Event) => {
    if (!(ev instanceof PointerEvent) || ev.pointerType !== 'touch') {
      return;
    }
    activeTouchesRef.current.add(ev.pointerId);
  }, []);
  const onUp = useCallback((ev: Event) => {
    if (!(ev instanceof PointerEvent) || ev.pointerType !== 'touch') {
      return;
    }
    activeTouchesRef.current.delete(ev.pointerId);
  }, []);

  // subscribe pointer lifecycle on the element

  useEvent('pointerdown', onDown, target);

  useEvent('pointerup', onUp, target);

  useEvent('pointercancel', onUp, target);

  useEvent('pointerleave', onUp, target);

  useEvent('lostpointercapture', onUp, target);

  // resolve the same element once per render

  const pinchRef: RefObject<HTMLElement> = targetRef.current
    ? (targetRef as unknown as RefObject<HTMLElement>)
    : fallbackRef;

  // derive pinch distance from react-use
  const { zoomingState, pinchState } = usePinchZoom(pinchRef);

  const applyPinchZoom = useCallback(
    (curDist: number) => {
      if (lastPinchDistRef.current === null || curDist <= 0) {
        lastPinchDistRef.current = curDist;
        return;
      }

      const EPS = 0.0001;
      let factor = curDist / Math.max(lastPinchDistRef.current, EPS); // relative distance change this frame
      if (!Number.isFinite(factor)) {
        factor = 1;
      }

      // curve sensitivity and clamp per-frame spikes
      factor = Math.pow(factor, sensitivity);
      const [MIN_F, MAX_F] = perFrameClamp;
      factor = Math.min(MAX_F, Math.max(MIN_F, factor));

      // apply
      const current = projectionRef.current.scale();
      const next = Math.max(minScale, Math.min(maxScale, current * factor));

      projectionRef.current.scale(next);
      emitter.emit({
        type: 'change-progress',
        timestamp: Date.now(),
        rotation: projectionRef.current.rotate(),
        scale: next,
      });

      lastPinchDistRef.current = curDist;
    },
    [sensitivity, perFrameClamp, projectionRef, emitter, minScale, maxScale],
  );

  useEffect(() => {
    const pinchingNow = activeTouchesRef.current.size >= 2;
    const pinchingBefore = wasPinchingRef.current;

    if (!pinchingBefore && pinchingNow) {
      beginPinch(); // emits change-start
    } else if (pinchingBefore && !pinchingNow) {
      endPinch(); // emits change-end
    }
    wasPinchingRef.current = pinchingNow;
  });

  useEffect(() => {
    const pinching = activeTouchesRef.current.size >= 2;

    if (!pinching || !zoomingState || pinchState <= 0) {
      lastPinchDistRef.current = null;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      return;
    }

    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = null;
        applyPinchZoom(pinchState);
      });
    }
  }, [zoomingState, pinchState, applyPinchZoom]);

  // RAF cancel on unmount
  useEffect(
    () => () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    },
    [],
  );

  return {
    /** use this to skip drag handlers while pinching */
    dragLockedRef,
  };
};
